/**
 * Abstract class to materialize instances of classes that are member
 * of a class hierarchy. Subclass it as a singleton for each class
 * hierarchy.
 *
 * Each concrete subclass of the hierarchy must define a class-side
 * `getType()` method. The factory finds the concrete class to
 * instantiate from a property of the JSON data and call
 * `materialize()` on that class.
 *
 * The factory is passed down in a context object to allow subclasses
 * `transformMaterializationData` methods to materialize nested
 * elements. Importing the factory class there would create a circular
 * dependency.
 *
 * @abstract
 */
export default class ModelFactory {
	materialize(json, context = {}) {
		const klass = this._findClassFromJson(json);
		const newContext = this._cloneContext(context);
		newContext.factory = this;
		return this._instantiate(klass, json, newContext);
	}

	materializeAll(jsonArray = [], context) {
		return jsonArray.map((json) => this.materialize(json, context));
	}

	/**
	 * Return all concrete classes that can be materialized by the
	 * receiver.
	 */
	allClasses() {
		return [];
	}

	_cloneContext(context) {
		return context.clone ? context.clone() : { ...context };
	}

	_getTypeFromJson(json) {
		return json.type;
	}

	_findClassFromJson(json) {
		const type = this._getTypeFromJson(json);

		return this._findClassFromType(type);
	}

	_findClassFromType(type) {
		const klass = this.allClasses().find(
			(each) => each.getType && each.getType() === type,
		);

		if (!klass) {
			throw `Cannot find class for type ${type}`;
		}

		return klass;
	}

	_instantiate(klass, json, context) {
		return klass.materialize(json, context);
	}
}
